import clsx from 'clsx';
import React from 'react';
import UploadArea from '../../../molecules/UploadArea/UploadArea';
import UploadFileItem from '../../../molecules/UploadFileItem/UploadFileItem';
import Dialog from '../Dialog/Dialog';
import useDialogUploadFile from './useDialogUploadFile';

export const DEFAULT_EXTENSIONS: string[] = [
  'pdf',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
  'svg',
  'mp4',
  'webm',
  'mov',
  'ogv',
  'mp3',
  'wav',
  'ogg',
  'aac',
];
const DEFAULT_MAX_SIZE_BYTES = 100 * 1024 * 1024;

export type DialogUploadFileProps = Readonly<{
  acceptedExtensions?: string[];
  ariaLabel?: string;
  buttonTextSuccess?: string;
  children?: React.ReactNode;
  className?: string;
  disclaimer?: React.ReactNode;
  extraActions?: React.ReactNode;
  isMultiple?: boolean;
  isOpen?: boolean;
  isSuccessDisabled?: boolean;
  isUploadAreaHidden?: boolean;
  isUploading?: boolean;
  maxSizeBytes?: number;
  onChangeFiles?: (files: File[]) => void;
  onClose: () => void;
  onSuccess?: (files: File[]) => void;
  title?: string;
  uploadProgress?: number;
}>;

function DialogUploadFile({
  acceptedExtensions = DEFAULT_EXTENSIONS,
  ariaLabel,
  buttonTextSuccess,
  children,
  className,
  disclaimer,
  extraActions,
  isMultiple = true,
  isOpen = false,
  isSuccessDisabled,
  isUploadAreaHidden = false,
  isUploading = false,
  maxSizeBytes = DEFAULT_MAX_SIZE_BYTES,
  onChangeFiles,
  onClose,
  onSuccess,
  title,
  uploadProgress = 0,
}: DialogUploadFileProps): React.JSX.Element {
  const {
    buttonTextSuccessText,
    errorText,
    handleFilesSelected,
    handleSuccess,
    hasFiles,
    makeHandleRemoveFile,
    rejectedFiles,
    selectedFiles,
    selectedFilesLabel,
    titleText,
    uploadAnnouncement,
  } = useDialogUploadFile({
    acceptedExtensions,
    buttonTextSuccess,
    isMultiple,
    isOpen,
    isUploading,
    maxSizeBytes,
    onChangeFiles,
    onSuccess,
    title,
  });

  return (
    <Dialog
      ariaLabel={ariaLabel}
      buttonTextSuccess={buttonTextSuccessText}
      className={clsx('dialog-upload-file', { 'dialog-upload-file--has-files': hasFiles }, className)}
      isDisabledSuccess={isUploading || (isSuccessDisabled ?? !hasFiles)}
      isOpen={isOpen}
      onCancel={onClose}
      onClose={onClose}
      onSuccess={handleSuccess}
      size="xl"
      title={titleText}
    >
      {children}
      {!isUploadAreaHidden && (
        <UploadArea
          acceptedExtensions={acceptedExtensions}
          className="dialog-upload-file__drop"
          isDisabled={isUploading}
          isMultiple={isMultiple}
          maxSizeBytes={maxSizeBytes}
          onFilesSelected={handleFilesSelected}
        />
      )}

      {extraActions && <div className="dialog-upload-file__extra-actions">{extraActions}</div>}

      {disclaimer && <div className="dialog-upload-file__disclaimer">{disclaimer}</div>}

      {rejectedFiles.length > 0 && (
        <p className="dialog-upload-file__error" role="alert">
          {errorText}: {rejectedFiles.join(', ')}
        </p>
      )}
      {hasFiles && (
        <ul aria-label={selectedFilesLabel} className="dialog-upload-file__list">
          {selectedFiles.map((file, index) => (
            <UploadFileItem
              key={`${file.name}-${index}`}
              file={file}
              isUploading={isUploading}
              onRemove={makeHandleRemoveFile(index)}
              progress={uploadProgress}
            />
          ))}
        </ul>
      )}
      <output className="sr-only">{uploadAnnouncement}</output>
    </Dialog>
  );
}

export default DialogUploadFile;
